import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/**
 * The typed section map for a case — three sections, all hand-written.
 *
 * The same story as campaigns, and by now the expected one: `CaseSheetDto.Sections` is
 * `IReadOnlyDictionary<string, object>` and nothing in the API references
 * `CaseCoreSectionDto` or its two siblings from an operation, so OpenAPI never sees
 * them. Transcribed from `CampaignManager.Api/Contracts/Cases/SectionDtos.cs`; the
 * captured fixture is the only thing that would notice them drifting.
 *
 * `CaseSummaryDto` and `CaseCandidateDto` *are* generated — they are the return types
 * of the list and available-characters endpoints, so they are reachable.
 */
export interface CaseSections {
  core: CaseCoreSection
  characters: CaseCharactersSection
  scenes: CaseScenesSection
}

/** Case identity plus the counts a client would otherwise derive. Section key `core`. */
export interface CaseCoreSection {
  id: number
  campaignId: number
  campaignName: string | null
  name: string
  description: string
  majorCharacterCount: number
  sceneCount: number
  createdAt: string
}

/** A character the case turns on, portrait already resolved to a URL. */
export interface CaseCharacter {
  /** The join row's id — what removal addresses, not the character's own id. */
  id: number
  characterId: number
  name: string
  type: Schema['CharacterType']
  portraitUrl: string | null
  addedAt: string
}

/** The case's major characters, by name. Section key `characters`. */
export interface CaseCharactersSection {
  characters: CaseCharacter[]
}

/**
 * One scene as the case sheet shows it: enough for the row and its badges, never the
 * scene's own contents — those are `GET /scenes/{id}`, which is the whole point of the
 * case ↔ scene cut.
 */
export interface CaseScene {
  id: number
  title: string
  timeOfDay: Schema['TimeOfDay']
  order: number
  locationId: number | null
  locationName: string | null
  summary: string
  aspectCount: number
  characterCount: number
  panelCount: number
  pageCount: number
  /** Whether a snapshot exists, without shipping the multi-kilobyte thing itself. */
  hasSnapshot: boolean
  createdAt: string
}

/** The case's scenes, in play order. Section key `scenes`. */
export interface CaseScenesSection {
  scenes: CaseScene[]
}

export type CaseSummary = Schema['CaseSummaryDto']
export type CaseCandidate = Schema['CaseCandidateDto']

export interface CaseSheet {
  caseId: number
  campaignId: number
  version: number
  /** Whether this caller may write. Members read; the Game Master writes. */
  isGameMaster: boolean
  sections: Partial<CaseSections>
}

export function toCaseSheet(dto: Schema['CaseSheetDto']): CaseSheet {
  if (dto.caseId === undefined) {
    throw new Error('The API returned a case sheet with no id.')
  }

  return {
    caseId: dto.caseId,
    campaignId: dto.campaignId ?? 0,
    version: dto.version ?? 0,
    isGameMaster: dto.isGameMaster ?? false,
    sections: (dto.sections ?? {}) as Partial<CaseSections>,
  }
}
